import React, { useState, useRef, useEffect, Fragment } from 'react';
import { API_BASE_URL } from '../../config';
import './ListingsAssistant.css';

// ─── Quick-start prompt chips ─────────────────────────────────────────────────
const QUICK_PROMPTS = [
    { label: '🛏 1 bedroom', text: "I'm looking for a 1-bedroom apartment" },
    { label: '👨‍👩‍👧 Family-sized', text: "I need a 3+ bedroom home for a family" },
    { label: '🐾 Pet-friendly', text: 'Do you have any pet-friendly units?' },
    { label: '🚗 With parking', text: "I need a place with parking included" },
    { label: '🧺 In-unit laundry', text: "I want in-unit washer/dryer" },
    { label: '💰 Under $1,500', text: 'Show me units under $1,500/month' },
];

// ─── Format markdown-ish text ─────────────────────────────────────────────────
const formatText = (text) => {
    const lines = text.split('\n');
    return lines.map((line, i) => {
        // Bold (**text**)
        const parts = line.split(/\*\*(.+?)\*\*/g).map((part, j) =>
            j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        );

        // Bullet points
        if (line.startsWith('- ') || line.startsWith('• ')) {
            return (
                <div key={i} className="la-bullet">
                    <span className="la-bullet-dot">•</span>
                    <span>{parts.slice(1)}</span>
                </div>
            );
        }

        // Numbered list
        if (/^\d+\.\s/.test(line)) {
            return <div key={i} className="la-numbered">{parts}</div>;
        }

        // Empty line → spacer
        if (line.trim() === '') {
            return <div key={i} className="la-spacer" />;
        }

        return (
            <Fragment key={i}>
                {parts}
                {i < lines.length - 1 && <br />}
            </Fragment>
        );
    });
};

// ─── Main Component ──────────────────────────────────────────────────────────
const ListingsAssistant = () => {
    const [open, setOpen]             = useState(false);
    const [input, setInput]           = useState('');
    const [messages, setMessages]     = useState([]);
    const [loading, setLoading]       = useState(false);
    const [hasGreeted, setHasGreeted] = useState(false);
    const [unread, setUnread]         = useState(false);
    const bottomRef                   = useRef(null);
    const inputRef                    = useRef(null);

    // Auto-scroll on new message
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    // Send the initial greeting from Alex once the panel opens
    useEffect(() => {
        if (open && !hasGreeted) {
            setHasGreeted(true);
            sendMessage('hello', /* silent = */ true);
        }
        if (open) {
            setUnread(false);
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const buildHistory = (msgs) =>
        msgs.map(m => ({
            role: m.role === 'assistant' ? 'assistant' : 'user',
            content: m.text,
        }));

    const sendMessage = async (overrideText, silent = false) => {
        const text = (overrideText ?? input).trim();
        if (!text) return;

        if (!silent) {
            setMessages(prev => [...prev, { role: 'user', text }]);
            setInput('');
        }

        setLoading(true);

        try {
            const res = await fetch(`${API_BASE_URL}/api/listings/assistant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: text,
                    history: buildHistory(messages),
                }),
            });

            if (!res.ok) throw new Error('Network error');
            const data = await res.json();

            setMessages(prev => [...prev, {
                role: 'assistant',
                text: data.response || "I'm not sure how to answer that. Try rephrasing?",
            }]);

            // Badge if panel is closed
            if (!open) setUnread(true);
        } catch {
            setMessages(prev => [...prev, {
                role: 'assistant',
                text: "Sorry, I couldn't connect right now. Please try again in a moment.",
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage();
    };

    const handleQuickPrompt = (text) => {
        setMessages(prev => [...prev, { role: 'user', text }]);
        sendMessage(text, /* silent = */ true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <>
            {/* ── Floating Bubble ──────────────────────────────── */}
            <button
                className={`la-bubble ${open ? 'open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="Open rental assistant"
            >
                {open ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        <circle cx="9" cy="10" r="1" fill="currentColor" />
                        <circle cx="12" cy="10" r="1" fill="currentColor" />
                        <circle cx="15" cy="10" r="1" fill="currentColor" />
                    </svg>
                )}
                {!open && unread && <span className="la-bubble-badge" />}
            </button>

            {/* Tooltip label (visible before first open) */}
            {!open && !hasGreeted && (
                <div className="la-tooltip">
                    <div className="la-tooltip-inner">
                        <span className="la-tooltip-emoji">🏠</span>
                        <div>
                            <strong>Find your perfect home</strong>
                            <span>Ask Alex, our AI rental guide</span>
                        </div>
                    </div>
                    <button className="la-tooltip-close" onClick={e => { e.stopPropagation(); setHasGreeted(true); }}>×</button>
                </div>
            )}

            {/* ── Chat Panel ────────────────────────────────────── */}
            {open && (
                <div className="la-panel">
                    {/* Header */}
                    <div className="la-panel-header">
                        <div className="la-header-avatar">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                            </svg>
                        </div>
                        <div className="la-header-info">
                            <div className="la-header-name">Alex</div>
                            <div className="la-header-subtitle">
                                <span className="la-online-dot" />
                                AI Rental Assistant
                            </div>
                        </div>
                        <button className="la-header-close" onClick={() => setOpen(false)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="la-messages">
                        {messages.length === 0 && !loading && (
                            <div className="la-intro">
                                <div className="la-intro-emoji">🏡</div>
                                <p>Tell me what you're looking for and I'll find the best matches from our listings!</p>
                            </div>
                        )}

                        {messages.map((msg, i) => (
                            <div key={i} className={`la-msg ${msg.role}`}>
                                {msg.role === 'assistant' && (
                                    <div className="la-msg-avatar">A</div>
                                )}
                                <div className="la-msg-bubble">
                                    {formatText(msg.text)}
                                </div>
                            </div>
                        ))}

                        {loading && (
                            <div className="la-msg assistant">
                                <div className="la-msg-avatar">A</div>
                                <div className="la-msg-bubble la-typing">
                                    <span /><span /><span />
                                </div>
                            </div>
                        )}

                        <div ref={bottomRef} />
                    </div>

                    {/* Quick prompt chips (shown only at start) */}
                    {messages.length <= 2 && !loading && (
                        <div className="la-chips">
                            {QUICK_PROMPTS.map((p, i) => (
                                <button
                                    key={i}
                                    className="la-chip"
                                    onClick={() => handleQuickPrompt(p.text)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <form className="la-input-area" onSubmit={handleSubmit}>
                        <textarea
                            ref={inputRef}
                            className="la-input"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Describe your ideal home…"
                            rows={1}
                            disabled={loading}
                        />
                        <button
                            className="la-send-btn"
                            type="submit"
                            disabled={!input.trim() || loading}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default ListingsAssistant;
